import { useMemo, useRef, useState } from 'react';
import type { PresignUploadRequest, PresignUploadResponse } from '../../lib/panelApi';
import { presignUpload } from '../../lib/panelApi';
import { safeHttpUrl } from '../../lib/safeUrl';

type Kind = 'audio_demo' | 'audio_full' | 'image' | 'pdf' | 'socialsnap' | 'subtitle';

type UploadItem = {
  id: string;
  file: File;
  kind: Kind;
  status: 'queued' | 'presigning' | 'uploading' | 'done' | 'error';
  error?: string;
  result?: PresignUploadResponse;
  copied?: boolean;
  progress: number; // 0–100
};

export function UploadsPage({ accessToken }: { accessToken: string }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [kind, setKind] = useState<Kind>('audio_demo');
  const [items, setItems] = useState<UploadItem[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const stats = useMemo(() => {
    const total = items.length;
    const done = items.filter((x) => x.status === 'done').length;
    const errors = items.filter((x) => x.status === 'error').length;
    return { total, done, errors };
  }, [items]);

  function addFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    if (!arr.length) return;
    setItems((prev) => [
      ...arr.map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        kind: kindFromFile(kind, f),
        status: 'queued' as const,
        progress: 0,
      })),
      ...prev,
    ]);
  }

  async function uploadOne(id: string) {
    const item = items.find((x) => x.id === id);
    if (!item) return;
    if (busyId) return;

    setBusyId(id);
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: 'presigning', error: undefined } : x)));

    const req: PresignUploadRequest = {
      kind: item.kind,
      file_name: item.file.name,
      content_type: contentTypeFor(item.file, item.kind),
      size_bytes: item.file.size,
    };

    let presigned: PresignUploadResponse;
    try {
      presigned = await presignUpload(accessToken, req);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Presign failed';
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: 'error', error: msg } : x)));
      setBusyId(null);
      return;
    }

    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: 'uploading', result: presigned, progress: 0 } : x)));

    try {
      await putToPresignedUrl(presigned.upload_url, presigned.headers, item.file, (pct) => {
        setItems((prev) => prev.map((x) => (x.id === id ? { ...x, progress: pct } : x)));
      });
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: 'done', progress: 100 } : x)));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: 'error', error: msg } : x)));
    } finally {
      setBusyId(null);
    }
  }

  async function uploadAll() {
    for (const it of items) {
      if (it.status === 'queued' || it.status === 'error') {
        // eslint-disable-next-line no-await-in-loop
        await uploadOne(it.id);
      }
    }
  }

  function clearDone() {
    setItems((prev) => prev.filter((x) => x.status !== 'done'));
  }

  function clearAll() {
    if (!confirm('Clear the upload queue?')) return;
    setItems([]);
  }

  return (
    <div className="card">
      <div className="pageHeader" style={{ marginBottom: 10 }}>
        <div>
          <div className="pageTitle">Uploads</div>
          <div className="pageSubtitle">Upload audio, images, or PDFs to Cloudflare R2 (via presigned URLs).</div>
        </div>
        <div className="row">
          <span className="pill">
            <span className="muted">Total</span>
            <span>{stats.total}</span>
          </span>
          <span className="pill">
            <span className="muted">Done</span>
            <span>{stats.done}</span>
          </span>
          <span className="pill">
            <span className="muted">Errors</span>
            <span>{stats.errors}</span>
          </span>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="toolbarGroup">
          <button className="btn btn-primary" type="button" onClick={() => inputRef.current?.click()}>
            Choose files
          </button>
          <button className="btn" type="button" onClick={uploadAll} disabled={items.length === 0 || !!busyId}>
            Upload all
          </button>
          <button className="btn btn-secondary" type="button" onClick={clearDone} disabled={items.every((x) => x.status !== 'done')}>
            Clear done
          </button>
          <button className="btn btn-danger" type="button" onClick={clearAll} disabled={items.length === 0 || !!busyId}>
            Clear all
          </button>
        </div>

        <div className="toolbarGroup" style={{ justifyContent: 'flex-end' }}>
          <div className="field" style={{ marginTop: 0, minWidth: 280 }}>
            <div className="label">Default bucket</div>
            <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              {BUCKET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        className="visuallyHidden"
        type="file"
        multiple
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <Dropzone
        onFiles={(files) => addFiles(files)}
        disabled={!!busyId}
        hint="Drop files here or use “Choose files”."
      />

      <div className="divider" style={{ margin: '14px 0' }} />

      {items.length === 0 ? (
        <div className="muted">No files in queue.</div>
      ) : (
        <div className="list">
          {items.map((it) => (
            <div key={it.id} className="item">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 900, fontSize: 14, wordBreak: 'break-word' }}>{it.file.name}</div>
                    <span className="pill" title="Bucket">{bucketLabel(it.kind)}</span>
                    <span className="pill" title="Status">{labelStatus(it.status)}</span>
                    <span className="pill" title="Size">{prettyBytes(it.file.size)}</span>
                  </div>

                  {/* Progress bar during upload */}
                  {(it.status === 'uploading' || it.status === 'presigning') ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          flex: 1,
                          height: 8,
                          borderRadius: 8,
                          background: 'rgba(10,25,47,0.08)',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${it.progress}%`,
                            height: '100%',
                            borderRadius: 8,
                            background: it.progress < 100
                              ? 'linear-gradient(90deg, #ccff00, #a6cc00)'
                              : '#22c55e',
                            transition: 'width 0.3s ease',
                          }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, minWidth: 40, textAlign: 'right' }}>
                          {it.status === 'presigning' ? 'Signing…' : `${it.progress}%`}
                        </span>
                      </div>
                    </div>
                  ) : null}

                  {/* After upload: show the UUID filename, copy button, and verify link */}
                  {it.status === 'done' && it.result?.key ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <code style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all', background: 'rgba(10,25,47,0.05)', padding: '4px 8px', borderRadius: 8 }}>
                          {it.result.key}
                        </code>
                        <button
                          className="btn btn-secondary"
                          type="button"
                          style={{ fontSize: 12, padding: '4px 10px' }}
                          onClick={() => {
                            navigator.clipboard.writeText(it.result!.key);
                            setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, copied: true } : x)));
                            setTimeout(() => {
                              setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, copied: false } : x)));
                            }, 2000);
                          }}
                        >
                          {it.copied ? 'Copied!' : 'Copy filename'}
                        </button>
                      </div>

                      {it.result.public_url ? (
                        <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <a
                            className="btn btn-secondary"
                            href={safeHttpUrl(it.result.public_url) ?? undefined}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 12, padding: '4px 10px', textDecoration: 'none' }}
                          >
                            Verify on Cloudflare ↗
                          </a>
                          <span className="muted" style={{ fontSize: 11, wordBreak: 'break-all' }}>{it.result.public_url}</span>
                        </div>
                      ) : (
                        <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                          Private bucket — no public URL available.
                        </div>
                      )}
                    </div>
                  ) : it.result?.key ? (
                    <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                      Key: <code>{it.result.key}</code>
                    </div>
                  ) : null}

                  {it.error ? (
                    <div style={{ marginTop: 10 }}>
                      <div className="errorBox">{it.error}</div>
                    </div>
                  ) : null}
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <select
                    value={it.kind}
                    onChange={(e) =>
                      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, kind: e.target.value as Kind } : x)))
                    }
                    disabled={!!busyId}
                    style={{ minWidth: 200 }}
                  >
                    {BUCKET_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button className="btn btn-primary" type="button" onClick={() => uploadOne(it.id)} disabled={!!busyId}>
                    {busyId === it.id ? 'Uploading…' : 'Upload'}
                  </button>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => setItems((prev) => prev.filter((x) => x.id !== it.id))}
                    disabled={!!busyId}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="item" style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 900 }}>Notes</div>
        <div className="muted" style={{ marginTop: 6, fontSize: 12, lineHeight: 1.6 }}>
          This panel uploads directly to R2 using a presigned PUT URL. Your R2 credentials never reach the browser.
          Ensure your bucket policy / public domain settings match your desired access level.
        </div>
      </div>
    </div>
  );
}

/** Bucket options — each kind maps to a dedicated R2 bucket. */
const BUCKET_OPTIONS: { value: Kind; label: string }[] = [
  { value: 'audio_demo', label: 'Audio Preview  ·  unplayist-audio-demos' },
  { value: 'audio_full', label: 'The Unplay  ·  unplayist-audio-full' },
  { value: 'image',      label: 'Image  ·  unplayist-images' },
  { value: 'socialsnap', label: 'Social Snap  ·  unplayist-socialsnaps' },
  { value: 'subtitle',   label: 'Subtitle (VTT/JSON)  ·  unplayist-audio-subtitles' },
  { value: 'pdf',        label: 'PDF  ·  unplayist-pdf' },
];

function bucketLabel(kind: Kind): string {
  return BUCKET_OPTIONS.find((o) => o.value === kind)?.label ?? kind;
}

function kindFromFile(fallback: Kind, file: File): Kind {
  const name = file.name.toLowerCase();
  // Subtitles are detected by extension first (browsers rarely set a type).
  if (name.endsWith('.vtt') || name.endsWith('.srt')) return 'subtitle';
  // A .json file only makes sense here as a subtitle cue-list.
  if (name.endsWith('.json') && (fallback === 'subtitle')) return 'subtitle';

  const t = (file.type || '').toLowerCase();
  if (t === 'application/pdf') return 'pdf';
  if (t === 'text/vtt') return 'subtitle';
  if (t.startsWith('image/')) return fallback === 'socialsnap' ? 'socialsnap' : 'image';
  if (t.startsWith('audio/')) return fallback === 'audio_full' ? 'audio_full' : 'audio_demo';
  if (name.endsWith('.pdf')) return 'pdf';
  if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.webp') || name.endsWith('.svg')) {
    return fallback === 'socialsnap' ? 'socialsnap' : 'image';
  }
  if (name.endsWith('.mp3') || name.endsWith('.m4a') || name.endsWith('.aac') || name.endsWith('.wav') || name.endsWith('.ogg')) {
    return fallback === 'audio_full' ? 'audio_full' : 'audio_demo';
  }
  return fallback;
}

/**
 * Resolve a Content-Type the backend will accept for the given kind.
 * Browsers often leave `file.type` empty for .vtt/.json, and the backend
 * validates subtitles strictly (text/vtt | application/json), so we infer
 * from the extension when needed.
 */
function contentTypeFor(file: File, kind: Kind): string {
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  const t = (file.type || '').toLowerCase();
  if (kind === 'subtitle') {
    if (ext === 'json') return 'application/json';
    return 'text/vtt';
  }
  if (kind === 'pdf') return 'application/pdf';
  if ((kind === 'image' || kind === 'socialsnap') && !t.startsWith('image/')) {
    if (ext === 'png') return 'image/png';
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
    if (ext === 'webp') return 'image/webp';
    if (ext === 'svg') return 'image/svg+xml';
  }
  return t || 'application/octet-stream';
}

function putToPresignedUrl(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    // Set headers from the presigned response
    for (const [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }

    // Track upload progress
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const pct = Math.round((e.loaded / e.total) * 100);
        onProgress(pct);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed: network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.send(file);
  });
}

function labelStatus(s: UploadItem['status']): string {
  switch (s) {
    case 'queued':
      return 'Queued';
    case 'presigning':
      return 'Presigning';
    case 'uploading':
      return 'Uploading';
    case 'done':
      return 'Done';
    case 'error':
      return 'Error';
  }
}

function prettyBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'] as const;
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const d = i === 0 ? 0 : v < 10 ? 2 : 1;
  return `${v.toFixed(d)} ${units[i]}`;
}

function Dropzone({
  onFiles,
  disabled,
  hint,
}: {
  onFiles: (files: FileList) => void;
  disabled: boolean;
  hint: string;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      className="item"
      style={{
        borderStyle: 'dashed',
        borderColor: over ? 'rgba(204, 255, 0, 0.85)' : 'rgba(10, 25, 47, 0.12)',
        background: over ? 'rgba(204, 255, 0, 0.08)' : 'rgba(255,255,255,0.55)',
        padding: 16,
        opacity: disabled ? 0.65 : 1,
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setOver(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        if (disabled) return;
        if (e.dataTransfer.files && e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
      }}
    >
      <div style={{ fontWeight: 900 }}>Dropzone</div>
      <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
        {hint}
      </div>
    </div>
  );
}

